import { CHAT_SESSION_PAGINATION, MESSAGE_ROLES } from '@/components/SearchAssistant/constants';
import { useCallback, useEffect } from 'react';

import { handleSubmitValidation } from '@/components/SearchAssistant/helpers/inputHelpers';
import { useCreateMessage } from '@/components/SearchAssistant/hooks/useCreateMessage';
import { useCreateChatSession } from '@/components/SearchAssistant/hooks/useCreateSession';
import { useGetMessages } from '@/components/SearchAssistant/hooks/useGetMessages';
import { useGetSessions } from '@/components/SearchAssistant/hooks/useGetSessions';
import { searchAssistantStore } from '@/stores/searchAssistantStore';

export const useLoadMoreSessions = () => {
  const { fetchMore } = useGetSessions();

  const sessionsPage = searchAssistantStore.use.sessionsPage();
  const hasMoreSessions = searchAssistantStore.use.hasMoreSessions();
  const loadingMoreSessions = searchAssistantStore.use.loadingMoreSessions();

  const loadMore = useCallback(async () => {
    if (!hasMoreSessions || loadingMoreSessions) return;

    try {
      searchAssistantStore.getState().setLoadingMoreSessions(true);

      const nextPage = sessionsPage + 1;

      const result = await fetchMore({
        variables: {
          pagination: {
            perPage: CHAT_SESSION_PAGINATION.DEFAULT_PER_PAGE,
            page: nextPage,
          },
        },
      });

      if (result.data?.chatSessions) {
        const { chatSessions, totalCount } = result.data.chatSessions;
        const currentSessionsCount = nextPage * CHAT_SESSION_PAGINATION.DEFAULT_PER_PAGE;
        const hasMore = currentSessionsCount < totalCount;

        searchAssistantStore.getState().setSessions(chatSessions);
        searchAssistantStore.getState().setHasMoreSessions(hasMore);
        searchAssistantStore.getState().incrementSessionsPage();
      }
    } catch (error) {
      throw new Error(`Failed to load more sessions: ${error.message}`);
    } finally {
      searchAssistantStore.getState().setLoadingMoreSessions(false);
    }
  }, [fetchMore, hasMoreSessions, loadingMoreSessions, sessionsPage]);

  return loadMore;
};

export const useCreateSessionWithStateUpdate = () => {
  const createSession = useCreateChatSession();

  const createSessionWithStateUpdate = useCallback(async () => {
    try {
      searchAssistantStore.getState().setCreatingSession(true);
      const response = await createSession();
      if (response?.data?.createChatSession) {
        searchAssistantStore.getState().storeAndSetCurrentSession(response.data.createChatSession);
        searchAssistantStore.getState().setCreatingSession(false);
      }
    } catch (error) {
      console.error('Error creating chat session:', error);
      searchAssistantStore.getState().setCreatingSession(false);
    }
  }, [createSession]);

  return createSessionWithStateUpdate;
};

const useSessionInitializer = () => {
  const { data: sessions } = useGetSessions();
  const createSession = useCreateChatSession();

  useEffect(() => {
    if (!sessions) {
      return;
    }

    if (sessions?.chatSessions?.chatSessions?.length) {
      searchAssistantStore.getState().setSessions(sessions.chatSessions.chatSessions);
    }

    if (sessions?.chatSessions?.totalCount === 0) {
      const handleCreateSession = async () => {
        try {
          searchAssistantStore.getState().setCreatingSession(true);
          const response = await createSession();
          if (response?.data?.createChatSession) {
            searchAssistantStore
              .getState()
              .storeAndSetCurrentSession(response.data.createChatSession);
          }
        } catch (error) {
          console.error(error);
        } finally {
          searchAssistantStore.getState().setCreatingSession(false);
        }
      };
      handleCreateSession();
      return;
    }
  }, [sessions, createSession]);
};

const useCurrentSessionMessages = () => {
  const currentSessionId = searchAssistantStore.use.currentSessionId();

  const { data: messagesResponse, error: messagesError } = useGetMessages(currentSessionId, {
    skip: !currentSessionId,
  });

  useEffect(() => {
    if (messagesResponse?.chatSessionMessages && currentSessionId) {
      const messages = messagesResponse?.chatSessionMessages || [];
      searchAssistantStore.getState().setMessages(currentSessionId, messages);
      const lastMessage = messages[0];

      if (lastMessage && lastMessage.role === MESSAGE_ROLES.ASSISTANT) {
        searchAssistantStore.getState().setAssistantTyping(currentSessionId, false);
      }
    }
  }, [messagesResponse?.chatSessionMessages, currentSessionId]);

  useEffect(() => {
    if (messagesError) {
      console.error(messagesError);
    }
  }, [messagesError]);
};

export const useOptimisticMessageCreation = () => {
  const currentSessionId = searchAssistantStore.use.currentSessionId();
  const { startPolling } = useGetMessages(currentSessionId);
  const createMessage = useCreateMessage();

  const createMessageWithOptimisticUpdate = useCallback(
    async (sessionId, messageContent) => {
      if (!messageContent.trim() || !sessionId) {
        return;
      }

      const validation = handleSubmitValidation(messageContent, sessionId);

      if (!validation.isValid) {
        validation.errors.forEach((error) => console.error(error));
        return;
      }

      const optimisticId = `optimistic-${Date.now()}`;

      const optimisticMessage = {
        role: 'user',
        content: validation.cleanInput,
        id: optimisticId,
      };

      searchAssistantStore.getState().addOptimisticMessage(sessionId, optimisticMessage);

      try {
        searchAssistantStore.getState().setInput('');
        const result = await createMessage(sessionId, validation.cleanInput);
        searchAssistantStore.getState().setAssistantTyping(sessionId, true);
        startPolling(1000);

        if (result.data?.createChatMessage) {
          searchAssistantStore
            .getState()
            .updateOptimisticMessage(sessionId, optimisticId, result.data.createChatMessage);
        } else {
          searchAssistantStore.getState().removeOptimisticMessage(sessionId, optimisticId);
        }
      } catch (err) {
        searchAssistantStore.getState().removeOptimisticMessage(sessionId, optimisticId);
      }
    },
    [createMessage],
  );

  return createMessageWithOptimisticUpdate;
};

export const useChatSetup = () => {
  useCurrentSessionMessages();
  useSessionInitializer();
};
